import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Autocomplete, TextField, Alert, CircularProgress, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import _ from 'lodash';
import qzPrintServices from 'app/services/qz-print-services';
import printerServices from 'app/services/printer-services';

const STORAGE_KEY = 'hermes:lastZebraPrinterId';

const ZebraPrintDialog = ({ isOpen, onClose, onConfirm, loading }) => {
    const [printers, setPrinters] = useState([]);
    const [selectedPrinterId, setSelectedPrinterId] = useState(null);
    const [qzAvailable, setQzAvailable] = useState(null);
    const [loadingPrinters, setLoadingPrinters] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setErrorMsg(null);
        setLoadingPrinters(true);

        Promise.all([
            qzPrintServices.isAvailable(),
            printerServices.getPrinters('?q[active_eq]=true&include=centre&page=1'),
        ])
            .then(([available, resp]) => {
                setQzAvailable(available);
                const list = resp?.data || [];
                setPrinters(list);
                const last = parseInt(localStorage.getItem(STORAGE_KEY), 10);
                const hit = _.find(list, { id: last }) || list[0] || null;
                setSelectedPrinterId(hit?.id || null);
            })
            .catch(() => {
                setErrorMsg('No se pudieron cargar las impresoras');
            })
            .finally(() => setLoadingPrinters(false));
    }, [isOpen]);

    const selectedPrinter = _.find(printers, { id: selectedPrinterId });

    const handlePrint = async () => {
        if (!selectedPrinter) return;
        localStorage.setItem(STORAGE_KEY, String(selectedPrinter.id));
        await onConfirm(selectedPrinter);
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <Typography variant="h3" color="primary">Imprimir en Zebra</Typography>
            </DialogTitle>
            <DialogContent>
                {qzAvailable === false && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        QZ Tray no está corriendo. Instalalo desde <a href="https://qz.io/download/" target="_blank" rel="noreferrer">qz.io/download</a> y abrilo antes de imprimir.
                    </Alert>
                )}
                {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
                {loadingPrinters ? (
                    <Stack alignItems="center" my={3}><CircularProgress /></Stack>
                ) : printers.length === 0 ? (
                    <Alert severity="info">
                        No hay impresoras configuradas. Agregalas en el mantenedor de Impresoras.
                    </Alert>
                ) : (
                    <Autocomplete
                        size="small"
                        options={printers}
                        getOptionLabel={(p) => `${p.name} (${p.ip}:${p.port})${p.centre?.name ? ` — ${p.centre.name}` : ''}`}
                        value={selectedPrinter || null}
                        onChange={(e, v) => setSelectedPrinterId(v?.id || null)}
                        renderInput={(params) => <TextField {...params} label="Impresora" sx={{ mt: 1, background: 'white' }} />}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton
                    loading={loading}
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<PrintOutlinedIcon />}
                    disabled={!selectedPrinter || qzAvailable === false}
                    onClick={handlePrint}>
                    Imprimir
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ZebraPrintDialog;
